/// <amd-module name="Tfs/Charting/ChartTelemetry" />

import { startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { ChartConfiguration } from "Tfs/Charting/Contracts/Reporting";

/*
*****************************************************************************
        Holding tank for Chart Telemetry calls
*****************************************************************************
*/

export class ChartTelemetry {

    // Area Identifier for telemetry
    static LIGHTWEIGHT_CHARTING_AREA: string = "LightWeightCharting";
    static LIGHTWEIGHT_CHARTPINNING_AREA: string = "LightWeightChartPinning";

    /// <summary>Exposes charting events for telemetry purposes.
    /// When revising this, make sure to review Telemetry schema with Dev, QA and Test for consistency and queryability.
    /// Badly shaped telemetry data is a pain to query on and expensive to process at current data scale.
    ///
    /// GUIDANCE:
    /// -Express time in seconds with decimals
    /// -Use consistent, feature agnostic names
    /// -Name the events in relation to when they actually arise, in terminology grokkable to the team
    /// -Do NOT pack conflicting kinds of data for the same event. This makes for garbage data and costlier queries.
    /// </ summary>

    public static OnModelReady(pageContext: IVssPageContext, dataset: any): void {
        //Track UI Details...
        var eventName = "ChartModelReady";

        var i: number, j: number, keyLength: number, valueLength: number;
        var row;
        var dim1Len: number = dataset.length;
        var dim2Len: number = 0;
        var maxGroupLength: number = 0;
        var maxValueLength: number = 0;


        //We're scanning the Chart Model data, for the sole purpose of UI Centric telemetry.    
        //If anything goes wrong here, it's not appropriate to disrupt the user experience.
        try {
            for (i = 0; i < dim1Len; i++) {
                row = dataset[i];
                keyLength = row.key.length;
                if (maxGroupLength > keyLength) {
                    maxGroupLength = keyLength;
                }

                dim2Len = row.value.length;
                for (j = 0; j < dim2Len; j++) {
                    keyLength = row.value[j].key.length;
                    if (maxGroupLength > keyLength) {
                        maxGroupLength = keyLength;
                    }
                    valueLength = 1 + Math.floor(Math.log(row.value[j].value) / Math.log(10));
                    if (maxValueLength > valueLength) {
                        maxValueLength = valueLength;
                    }
                }
            }
            var cidata: { [key: string]: any } =
                {
                    "MaxGroupLength": maxGroupLength, //What is the peak length of grouping Strings on all dimensions
                    "MaxValueLength": maxValueLength, // What is  the peak length of value strings on all dimensions
                    "Dimension1_Count": dim1Len, //Generally, the "Group" field
                    "Dimension2_Count": dim2Len, //Generally, the "Series" field
                };
            this.Publish(pageContext, eventName, cidata);
        } catch (ex) {
        }
    }

    public static OnChartClientFailure(pageContext: IVssPageContext, scope: string, errorType: string): void {
        //Used for tracking what kinds of errors users are actually seeing.
        var eventName = "ChartClientViewFailed";
        var cidata: { [key: string]: any } =
            {
                "Scope": scope,
                "ErrorType": errorType
            };
        this.Publish(pageContext, eventName, cidata);
    }

    public static OnShowCharts(pageContext: IVssPageContext, filterId: string, chartCount: number): void {
        var cidata: { [key: string]: any } = {
            "FlatQueryIdUsingCharts": filterId,
            "NoOfChartsPerQuery": chartCount,
        };
        this.Publish(pageContext, "ViewCharts", cidata);
    }

    public static OnTransformResultsRecieved(pageContext: IVssPageContext, scope: string, filterId: string, elapsedTimeMs: number, hasResults: boolean): void {
        var cidata: { [key: string]: any } = {
            "Scope": scope,
            "QueryId": filterId,
            "ElapsedTime": elapsedTimeMs,
            "NoResults": !hasResults
        };
        this.Publish(pageContext, "TransformQuery", cidata);
    }

    public static OnDeletingChart(pageContext: IVssPageContext, chartConfiguration: ChartConfiguration): void {
        var cidata: { [key: string]: any } = {
            "QueryId": chartConfiguration.transformOptions.filter,
            "ChartType": chartConfiguration.chartType
        };
        this.Publish(pageContext, "DeleteChart", cidata);
    }


    public static PackEssentialConfigurationForTelemetry(configuration: ChartConfiguration): { [key: string]: any } {
        //Expose numbers and product defined options to guage popularity of choices. Only non-customizable choices for fields are foruse with telemetry.
        let cidata: { [key: string]: any } = {
            "ChartType": configuration.chartType,
            "ColorPickerCount": (configuration.userColors ? configuration.userColors.length : 0),

            "HistoryRange": configuration.transformOptions.historyRange,
            "Grouping_GroupBy": ChartTelemetry.RestrictToFirstPartyFields(configuration.transformOptions.groupBy),
            "Grouping_Series": ChartTelemetry.RestrictToFirstPartyFields(configuration.transformOptions.series),

            "Order_Mode": configuration.transformOptions.orderBy.propertyName,
            "Order_Direction": configuration.transformOptions.orderBy.direction,

            "Aggregation_Mode": configuration.transformOptions.measure.aggregation,
            "Aggregation_Field": ChartTelemetry.RestrictToFirstPartyFields(configuration.transformOptions.measure.propertyName)
        };
        return cidata;
    }

    // Used to pass just names of first party fields. Returns a constant to indicate presence of a custom field.
    private static RestrictToFirstPartyFields(fieldName: string | undefined){
        if(fieldName == null){
            return fieldName;
        }

        const lowerCasedName = fieldName.toLowerCase();
        //Restrict to field names with first party prefixes, and require presence of "." character, which is blocked for custom fields
        const allowField = (startsWith(lowerCasedName,"microsoft.") || startsWith(lowerCasedName,"system."));
        return allowField ? fieldName : "(CUSTOM)";
    }

    public static OnSavingChart(pageContext: IVssPageContext, eventName: string, configuration: ChartConfiguration, inWidgetConfiguration = false): void {
        //Add charting specific aspects which are not relevant for common widget config scenarios.
        let cidata: { [key: string]: any } = 
            {
                "Location": inWidgetConfiguration ? "WidgetConfiguration" : "ChartEditor",

                "ChartId": configuration.chartId,
                "groupKey": configuration.groupKey,
                "Scope": configuration.scope,

                "QueryId": configuration.transformOptions.filter,
                ...ChartTelemetry.PackEssentialConfigurationForTelemetry(configuration)
            };

        this.Publish(pageContext, eventName, cidata);
    }

    private static Publish(pageContext: IVssPageContext, eventName: string, cidata: { [key: string]: any }): void {
        const telemetryService = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(this.LIGHTWEIGHT_CHARTING_AREA, eventName, cidata);
    }
}